import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Checkbox,
    IconButton,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SystemMailCampaign } from "../../model/SystemMailCampaign";
import { SystemMailSchedule } from "../../model/SystemMailSchedule";
import { ReceiverGroup } from "../../model/ReceiverGroup";
import { SystemMailWithoutBody } from "../../model/SystemMail";
import {
    createSystemMailCampaign,
    updateSystemMailCampaign,
    deleteSystemMailCampaign,
} from "../../api/systemMailCampaignClient";
import { Status } from "../../model/Campaign";
import classes from "../../styling/components/Editor.module.css";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";

dayjs.extend(utc);

interface ScheduleRow {
    id: string;
    systemMailTemplateId: string;
    sendTime: Dayjs | null;
}

function toScheduleRow(s: SystemMailSchedule, index: number): ScheduleRow {
    return {
        id: `schedule-${index}-${s.scheduleId ?? index}`,
        systemMailTemplateId: s.systemMailTemplateId,
        sendTime: s.sendTime ? dayjs(s.sendTime) : null,
    };
}

function toSchedule(row: ScheduleRow): Omit<SystemMailSchedule, "scheduleId" | "sent"> {
    return {
        systemMailTemplateId: row.systemMailTemplateId,
        sendTime: row.sendTime ? row.sendTime.toDate() : new Date(),
    };
}

interface Props {
    method: "POST" | "PUT";
    campaign?: SystemMailCampaign;
    receiverGroups: ReceiverGroup[];
    systemMailTemplates: SystemMailWithoutBody[];
}

const SystemMailCampaignEditor: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { customerId } = useParams();
    const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
    const readOnly =
        props.method === "PUT" &&
        props.campaign != null &&
        props.campaign.status !== Status.IN_CONFIGURATION;

    const [name, setName] = useState(props.campaign?.name ?? "");
    const [checkedReceiverGroups, setCheckedReceiverGroups] = useState<boolean[]>(
        props.receiverGroups.map((rg) =>
            props.campaign?.receiverGroups?.some(
                (c) => c.receiverGroupId === rg.receiverGroupId
            ) ?? false
        )
    );
    const [schedules, setSchedules] = useState<ScheduleRow[]>(() => {
        if (!props.campaign?.schedules?.length)
            return [{ id: "schedule-0", systemMailTemplateId: "", sendTime: null }];
        const sorted = [...props.campaign.schedules].sort((a, b) => {
            const tA = a.sendTime ? new Date(a.sendTime).getTime() : Number.MAX_SAFE_INTEGER;
            const tB = b.sendTime ? new Date(b.sendTime).getTime() : Number.MAX_SAFE_INTEGER;
            return tA - tB;
        });
        return sorted.map((s, i) => toScheduleRow(s, i));
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const handleReceiverGroupChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        setCheckedReceiverGroups((prev) =>
            prev.map((c, i) => (i === index ? event.target.checked : c))
        );
    };

    const addSchedule = () => {
        setSchedules((prev) => [
            ...prev,
            {
                id: `schedule-${Date.now()}`,
                systemMailTemplateId: "",
                sendTime: null,
            },
        ]);
    };

    const removeSchedule = (id: string) => {
        setSchedules((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : prev));
    };

    const updateSchedule = (
        id: string,
        field: keyof ScheduleRow,
        value: string | Dayjs | null
    ) => {
        setSchedules((prev) =>
            prev.map((s) =>
                s.id === id ? { ...s, [field]: value } : s
            )
        );
    };

    const buildPayload = (): SystemMailCampaign => {
        const receiverGroups = props.receiverGroups
            .filter((_, i) => checkedReceiverGroups[i])
            .map((rg) => ({ receiverGroupId: rg.receiverGroupId!, name: rg.name }));
        const validSchedules = schedules
            .filter((s) => s.systemMailTemplateId && s.sendTime?.isValid())
            .map(toSchedule);
        return {
            ...(props.campaign?.campaignId && { campaignId: props.campaign.campaignId }),
            name: name.trim(),
            status: props.campaign?.status ?? Status.IN_CONFIGURATION,
            receiverGroups,
            schedules: validSchedules,
        };
    };

    const validate = (): string | null => {
        if (!name.trim()) return t("systemMailCampaign.editor.nameError");
        if (!checkedReceiverGroups.some(Boolean))
            return t("systemMailCampaign.editor.receiverGroupError");
        const validSchedules = schedules.filter(
            (s) => s.systemMailTemplateId && s.sendTime?.isValid()
        );
        if (validSchedules.length === 0)
            return t("systemMailCampaign.editor.scheduleError");
        return null;
    };

    const submitHandler = async () => {
        const err = validate();
        if (err) {
            setSaveError(err);
            return;
        }
        setSaveError(null);
        setIsSubmitting(true);
        try {
            const payload = buildPayload();
            if (props.method === "POST") {
                await createSystemMailCampaign(payload, customerId!);
            } else {
                await updateSystemMailCampaign(
                    payload,
                    props.campaign!.campaignId!,
                    customerId!
                );
            }
            navigate(`/customers/${customerId}/system-mail-campaigns`);
        } catch(error) {
            setSaveError(t("systemMailCampaign.table.saveError") + " Error: " + error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelHandler = () => {
        navigate(`/customers/${customerId}/system-mail-campaigns`);
    };

    const deleteHandler = () => {
        openConfirmationDialog(
            t("systemMailCampaign.editor.deleteTitle"),
            t("systemMailCampaign.editor.deleteContent"),
            async () => {
                if (!props.campaign?.campaignId || !customerId) return;
                try {
                    await deleteSystemMailCampaign(props.campaign.campaignId, customerId);
                    navigate(`/customers/${customerId}/system-mail-campaigns`);
                } catch {
                    setSaveError(t("systemMailCampaign.table.deleteError"));
                }
            }
        );
    };

    return (
        <>
            {confirmationDialog}
            <Card>
                <CardContent>
                    <Box className={classes.form}>
                        <div className={classes.subtitle}>
                            {t("systemMailCampaign.editor.name")}
                        </div>
                        <div className={classes.nameInput}>
                            <TextField
                                fullWidth
                                label={t("systemMailCampaign.editor.name")}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                helperText={t("systemMailCampaign.editor.nameHelper")}
                                disabled={readOnly}
                            />
                        </div>

                        <Divider />

                        <div className={classes.subtitle}>
                            {t("systemMailCampaign.editor.receiverGroups")}
                        </div>
                        <FormControl component="fieldset">
                            <FormGroup
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    gap: "10px",
                                }}
                            >
                                {props.receiverGroups.map((rg, index) => (
                                    <FormControlLabel
                                        key={rg.receiverGroupId}
                                        label={rg.name}
                                        control={
                                            <Checkbox
                                                checked={checkedReceiverGroups[index]}
                                                onChange={(e) =>
                                                    handleReceiverGroupChange(e, index)
                                                }
                                                disabled={readOnly}
                                            />
                                        }
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>

                        <Divider />

                        <div className={classes.subtitle}>
                            {t("systemMailCampaign.editor.schedules")}
                        </div>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            {[...schedules]
                                .sort((a, b) => {
                                    const tA = a.sendTime?.valueOf() ?? Number.MAX_SAFE_INTEGER;
                                    const tB = b.sendTime?.valueOf() ?? Number.MAX_SAFE_INTEGER;
                                    return tA - tB;
                                })
                                .map((row) => (
                                <Box
                                    key={row.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        mb: 2,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <FormControl sx={{ minWidth: 220 }}>
                                        <Select
                                            displayEmpty
                                            value={row.systemMailTemplateId}
                                            onChange={(e) =>
                                                updateSchedule(
                                                    row.id,
                                                    "systemMailTemplateId",
                                                    e.target.value
                                                )
                                            }
                                            renderValue={(v) =>
                                                v
                                                    ? props.systemMailTemplates.find(
                                                          (tpl) => tpl.templateId === v
                                                      )?.name ?? v
                                                    : t("systemMailCampaign.editor.template")
                                            }
                                            disabled={readOnly}
                                        >
                                            <MenuItem value="">
                                                <em>{t("systemMailCampaign.editor.template")}</em>
                                            </MenuItem>
                                            {props.systemMailTemplates.map((tpl) => (
                                                <MenuItem
                                                    key={tpl.templateId}
                                                    value={tpl.templateId}
                                                >
                                                    {tpl.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <DateTimePicker
                                        label={t("systemMailCampaign.editor.sendTime")}
                                        value={row.sendTime}
                                        onChange={(v) =>
                                            updateSchedule(row.id, "sendTime", v)
                                        }
                                        ampm={false}
                                        disabled={readOnly}
                                        slotProps={{
                                            textField: { size: "small", sx: { minWidth: 220 } },
                                        }}
                                    />
                                    {!readOnly && (
                                        <IconButton
                                            aria-label="delete schedule"
                                            onClick={() => removeSchedule(row.id)}
                                            disabled={schedules.length <= 1}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Box>
                            ))}
                        </LocalizationProvider>
                        {!readOnly && (
                            <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={addSchedule}
                                sx={{ mb: 2 }}
                            >
                                {t("systemMailCampaign.editor.addSchedule")}
                            </Button>
                        )}

                        {saveError && (
                            <Alert
                                severity="error"
                                onClose={() => setSaveError(null)}
                                sx={{ mb: 2 }}
                            >
                                {saveError}
                            </Alert>
                        )}

                        <Divider />

                        <div>
                            {!readOnly && (
                                <>
                                    {props.method === "PUT" && (
                                        <Button
                                            type="button"
                                            className={classes.delete}
                                            variant="contained"
                                            onClick={deleteHandler}
                                            disabled={isSubmitting}
                                        >
                                            {t("systemMailCampaign.editor.deleteCampaign")}
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        onClick={cancelHandler}
                                        disabled={isSubmitting}
                                    >
                                        {t("systemMailCampaign.editor.cancel")}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="contained"
                                        onClick={submitHandler}
                                        disabled={isSubmitting}
                                    >
                                        {t("systemMailCampaign.editor.save")}
                                    </Button>
                                </>
                            )}
                            {readOnly && (
                                <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={cancelHandler}
                                >
                                    {t("systemMailCampaign.editor.cancel")}
                                </Button>
                            )}
                        </div>
                    </Box>
                </CardContent>
            </Card>
        </>
    );
};

export default SystemMailCampaignEditor;
